import { useMemo } from 'react';
import { useFeatureFlagContext } from './FeatureFlagContext';
import type { UseFeatureFlagResult, Environment } from './types';

/**
 * Hook for programmatically checking a single feature flag.
 *
 * @param featureName - The name of the feature flag to check
 * @returns Object containing isEnabled, flag definition, and hasRoleAccess
 *
 * @example
 * const { isEnabled } = useFeatureFlag('new-dashboard');
 *
 * if (isEnabled) {
 *   // Show new dashboard features
 * }
 *
 * @example
 * // Access the full flag definition
 * const { isEnabled, flag, hasRoleAccess } = useFeatureFlag('admin-feature');
 * console.log(flag?.description); // "Admin-only feature"
 */
export function useFeatureFlag(featureName: string): UseFeatureFlagResult {
    const { isEnabled, getFlag, hasRoleAccess } = useFeatureFlagContext();

    return useMemo(
        () => ({
            isEnabled: isEnabled(featureName),
            flag: getFlag(featureName),
            hasRoleAccess: hasRoleAccess(featureName),
        }),
        [featureName, isEnabled, getFlag, hasRoleAccess]
    );
}

/**
 * Hook for getting all feature flags at once.
 * Useful for debugging, feature flag admin panels, or components
 * that need to check multiple flags.
 *
 * @returns Object containing all flags and current environment
 *
 * @example
 * const { flags, environment } = useAllFeatureFlags();
 * console.log(flags); // { 'new-dashboard': true, 'dark-mode': false, ... }
 * console.log(environment); // 'development'
 */
export function useAllFeatureFlags(): {
    flags: Record<string, boolean>;
    environment: Environment;
} {
    const { getAllFlags, environment } = useFeatureFlagContext();

    return useMemo(
        () => ({
            flags: getAllFlags(),
            environment,
        }),
        [getAllFlags, environment]
    );
}
